//MMT3 Slice
import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { axiosInstance } from "../../AxiosInterceptor/axiosInterceptor";
import { MmtSkipDetail, MmtLoadDetail, MntManualDetails, MntCompoundDetails } from "../../Table/TableQueryColumn";

const initialState = {
    skipData: {
        totalCount: 0,
        page: 0,
        pageSize: 50,
        collectionName: "MmtSkips",
        data: [],
        loading: false,
        error: false,
        lastRefreshedDate: ''
    },
    loadData: {
        totalCount: 0,
        page: 0,
        pageSize: 50,
        collectionName: "MmtLoads",
        data: [],
        loading: false,
        error: false,
        lastRefreshedDate: ''
    },
    manualData: {
        totalCount: 0,
        page: 0,
        pageSize: 50,
        collectionName: "MmtManuals",
        data: [],
        loading: false,
        error: false,
        lastRefreshedDate: ''
    },
    compoundData: {
        totalCount: 0,
        page: 0,
        pageSize: 50,
        collectionName: "MmtCompounds",
        data: [],
        loading: false,
        error: false,
        lastRefreshedDate: ''
    }
}

export const getMmtSkipDetailsData = createAsyncThunk('data/getMmtSkipDetailsData',
    async (data, { rejectWithValue }) => {
        try {
            let queryMmtSkipDetails =
                `query {
                GetMmtSkipDetails(
                collectionName: "MmtSkips"
                `;

            queryMmtSkipDetails += MmtSkipDetail

            const response = await axiosInstance.post(process.env.REACT_APP_MMT_REPORTS,
                JSON.stringify({ query: queryMmtSkipDetails, variables: {} })
            );

            if (response.data.errors && response.data.errors.length > 0) {
                const errorMessages = response.data.errors.map(err => err.message).join(', ');
                return rejectWithValue(errorMessages);
            }

            return JSON.stringify(response);
        } catch (err) {
            return rejectWithValue(err.message || 'Unexpected error');
        }
    }
);

export const getMmtLoadDetailsData = createAsyncThunk('data/getMmtLoadDetailsData',
    async (data, { rejectWithValue }) => {
        try {
            let queryMmtLoadDetails =
                `query {GetMmtLoadDetails(
                    collectionName: "MmtLoads"
                `;

            queryMmtLoadDetails += MmtLoadDetail

            const response = await axiosInstance.post(process.env.REACT_APP_MMT_REPORTS,
                JSON.stringify({ query: queryMmtLoadDetails, variables: {} })
            );

            if (response.data.errors && response.data.errors.length > 0) {
                const errorMessages = response.data.errors.map(err => err.message).join(', ');
                return rejectWithValue(errorMessages);
            }

            return JSON.stringify(response);
        } catch (err) {
            return rejectWithValue(err.message || 'Unexpected error');
        }
    }
);

export const getMmtManualDetailsData = createAsyncThunk('data/getMmtManualDetailsData',
    async (data, { rejectWithValue }) => {
        try {
            let queryMmtManualDetails =
                `query {GetMmtManualDetails(
                collectionName: "MmtManuals"
                `;

            queryMmtManualDetails += MntManualDetails

            const response = await axiosInstance.post(process.env.REACT_APP_MMT_REPORTS,
                JSON.stringify({ query: queryMmtManualDetails, variables: {} })
            );

            if (response.data.errors && response.data.errors.length > 0) {
                const errorMessages = response.data.errors.map(err => err.message).join(', ');
                return rejectWithValue(errorMessages);
            }

            return JSON.stringify(response);
        } catch (err) {
            return rejectWithValue(err.message || 'Unexpected error');
        }
    }
);

export const getMmtCompoundDetailsData = createAsyncThunk('data/getMmtCompoundDetailsData',
    async (data, { rejectWithValue }) => {
        try {
            let queryMmtCompoundDetails =
                `query { GetMmtCompoundDetails(
                collectionName: "MmtCompounds"
                `;

            queryMmtCompoundDetails += MntCompoundDetails

            const response = await axiosInstance.post(process.env.REACT_APP_MMT_REPORTS,
                JSON.stringify({ query: queryMmtCompoundDetails, variables: {} })
            );

            if (response.data.errors && response.data.errors.length > 0) {
                const errorMessages = response.data.errors.map(err => err.message).join(', ');
                return rejectWithValue(errorMessages);
            }

            return JSON.stringify(response);
        } catch (err) {
            return rejectWithValue(err.message || 'Unexpected error');
        }
    }
);

const mmt3Slice = createSlice({
    name: 'mmt3',
    initialState,
    reducers: {
        clearData: (state) => {
            state.loadData = initialState.loadData
            state.skipData = initialState.skipData
            state.manualData = initialState.manualData
            state.compoundData = initialState.compoundData
        }
    },
    extraReducers: (builder) => {
        builder
            .addCase(getMmtSkipDetailsData.pending, (state) => {
                state.skipData.loading = true;
                state.skipData.error = false;
            })
            .addCase(getMmtSkipDetailsData.fulfilled, (state, action) => {
                const response = JSON.parse(action.payload);
                state.skipData.error = false;
                state.skipData.loading = false;
                state.skipData.lastRefreshedDate = response?.data?.data?.GetMmtSkipDetails?.lastRefreshedDate ?? '';
                state.skipData.data = response.data.data.GetMmtSkipDetails.data;
            })
            .addCase(getMmtSkipDetailsData.rejected, (state, action) => {
                state.skipData.loading = false;
                state.skipData.error = true;
            })
            .addCase(getMmtLoadDetailsData.pending, (state) => {
                state.loadData.loading = true;
                state.loadData.error = false;
            })
            .addCase(getMmtLoadDetailsData.fulfilled, (state, action) => {
                const response = JSON.parse(action.payload);
                state.loadData.error = false;
                state.loadData.loading = false;
                state.loadData.lastRefreshedDate = response?.data?.data?.GetMmtLoadDetails?.lastRefreshedDate ?? '';
                state.loadData.data = response.data.data.GetMmtLoadDetails.data;
            })
            .addCase(getMmtLoadDetailsData.rejected, (state, action) => {
                state.loadData.loading = false;
                state.loadData.error = true;
            })
            .addCase(getMmtManualDetailsData.pending, (state) => {
                state.manualData.loading = true;
                state.manualData.error = false;
            })
            .addCase(getMmtManualDetailsData.fulfilled, (state, action) => {
                const response = JSON.parse(action.payload);
                state.manualData.error = false;
                state.manualData.loading = false;
                state.manualData.lastRefreshedDate = response?.data?.data?.GetMmtManualDetails?.lastRefreshedDate ?? '';
                state.manualData.data = response.data.data.GetMmtManualDetails.data;
            })
            .addCase(getMmtManualDetailsData.rejected, (state, action) => {
                state.manualData.loading = false;
                state.manualData.error = true;
            })
            .addCase(getMmtCompoundDetailsData.pending, (state) => {
                state.compoundData.loading = true;
                state.compoundData.error = false;
            })
            .addCase(getMmtCompoundDetailsData.fulfilled, (state, action) => {
                const response = JSON.parse(action.payload);
                state.compoundData.error = false;
                state.compoundData.loading = false;
                state.compoundData.lastRefreshedDate = response?.data?.data?.GetMmtCompoundDetails?.lastRefreshedDate ?? '';
                state.compoundData.data = response.data.data.GetMmtCompoundDetails.data;
            })
            .addCase(getMmtCompoundDetailsData.rejected, (state, action) => {
                state.compoundData.loading = false;
                state.compoundDataerror = true;
            })
    }
})

export const { clearData } = mmt3Slice.actions;
export default mmt3Slice.reducer